import type React from 'react';
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { updateBot, uploadBotPicture } from '../../api/admin';
import { Avatar } from '../../components/ui';
import type { BotPersonality, BotRow } from '../../types/admin';

const PERSONALITIES: BotPersonality[] = ['balanced', 'aggressive', 'defensive', 'combo'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 512;

/**
 * Downscales an image to at most MAX_DIMENSION and re-encodes it as JPEG.
 *
 * Keeps a 4MB phone photo from being shipped as ~5.3MB of base64 for what is
 * rendered as a 40px avatar. Returns a bare base64 payload (no data: prefix) —
 * the server accepts either.
 */
async function toDownscaledBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Arquivo de imagem inválido.'));
    img.src = dataUrl;
  });

  const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl.split(',')[1] ?? '';
  ctx.drawImage(image, 0, 0, width, height);

  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1] ?? '';
}

export function BotProfileForm({
  bot,
  onSaved,
}: {
  bot: BotRow;
  onSaved: (bot: BotRow) => void;
}): React.JSX.Element {
  const [displayName, setDisplayName] = useState(bot.displayName);
  const [userName, setUserName] = useState(bot.userName);
  const [level, setLevel] = useState(bot.level ?? 1);
  const [personality, setPersonality] = useState<BotPersonality>(
    bot.botPersonality ?? 'balanced',
  );
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const result = await updateBot(bot.id, {
        displayName: displayName.trim(),
        userName: userName.trim(),
        level,
        botPersonality: personality,
      });
      onSaved(result.bot);
      setStatus('Perfil salvo.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setStatus(null);

    if (!file.type.startsWith('image/')) {
      setError('Selecione um arquivo de imagem.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('A imagem deve ter menos de 5 MB.');
      return;
    }

    setUploading(true);
    try {
      const base64 = await toDownscaledBase64(file);
      const result = await uploadBotPicture(bot.id, base64);
      onSaved(result.bot);
      setStatus('Imagem atualizada.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no envio da imagem.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <form className="panel" onSubmit={(e) => void onSubmit(e)}>
      <h2 className="panel__title">Perfil</h2>

      <div className="profile-row">
        <Avatar src={bot.profileImgUrl} name={bot.displayName} size={72} />
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="visually-hidden"
            onChange={(e) => void onPickFile(e)}
          />
          <button
            type="button"
            className="btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Enviando…' : 'Trocar imagem'}
          </button>
          <p className="field__hint">JPEG/PNG, até 5 MB. Redimensionada para 512 px.</p>
        </div>
      </div>

      <label className="field">
        <span>Nome de exibição</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={32}
          required
        />
      </label>

      <label className="field">
        <span>Nome de usuário</span>
        <input value={userName} onChange={(e) => setUserName(e.target.value)} required />
      </label>

      <div className="field-row">
        <label className="field">
          <span>Nível</span>
          <input
            type="number"
            min={1}
            max={100}
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
            required
          />
        </label>
        <label className="field">
          <span>Estilo</span>
          <select
            value={personality}
            onChange={(e) => setPersonality(e.target.value as BotPersonality)}
          >
            {PERSONALITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {status ? <div className="form-ok">{status}</div> : null}

      <button type="submit" className="btn btn--primary" disabled={busy}>
        {busy ? 'Salvando…' : 'Salvar perfil'}
      </button>
    </form>
  );
}
